import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryForm from '../../components/obituary/ObituaryForm';
import ProgressBar from '../../components/common/ProgressBar';
import Button from '../../components/common/Button';

/**
 * ObituaryCreateStep2 - 부고 작성 2단계 (장례식장 정보 입력)
 * 모바일 전용 다단계 부고 작성 페이지
 */
const ObituaryCreateStep2 = () => {
  const navigate = useNavigate();

  // localStorage에서 기존 데이터 불러오기 (렌더 중 navigate 금지 → useEffect로 이동)
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('obituaryDraft');
    if (!saved) return {};
    try {
      return JSON.parse(saved);
    } catch (e) {
      return {};
    }
  });

  const [errors, setErrors] = useState({});

  // 이전 단계(Step1) 완료 검증: 고인 정보가 없으면 Step1으로 리다이렉트
  useEffect(() => {
    const saved = localStorage.getItem('obituaryDraft');
    let parsed = null;
    try {
      parsed = saved ? JSON.parse(saved) : null;
    } catch (e) {
      parsed = null;
    }
    if (!parsed || !parsed.deceasedName || !parsed.relationship) {
      navigate('/obituaries/create', { replace: true });
    }
  }, [navigate]);

  // 폼 데이터 변경 핸들러
  const handleChange = (newData) => {
    setFormData(newData);
    // localStorage에 임시 저장
    localStorage.setItem('obituaryDraft', JSON.stringify(newData));
    // 에러 초기화
    if (errors[Object.keys(newData).find(key => newData[key] !== formData[key])]) {
      setErrors({});
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    if (!formData.funeralHome?.trim()) {
      newErrors.funeralHome = '장례식장명을 입력해주세요.';
    }

    if (!formData.funeralAddress?.trim()) {
      newErrors.funeralAddress = '장례식장 주소를 입력해주세요.';
    }

    if (!formData.funeralRoom?.trim()) {
      newErrors.funeralRoom = '호실을 입력해주세요.';
    }

    if (!formData.departureDate) {
      newErrors.departureDate = '발인일을 입력해주세요.';
    }

    if (!formData.departureTime) {
      newErrors.departureTime = '발인시간을 입력해주세요.';
    }

    // 발인일이 사망일보다 이전인 경우
    if (formData.deceasedDeathDate && formData.departureDate) {
      if (new Date(formData.departureDate) < new Date(formData.deceasedDeathDate)) {
        newErrors.departureDate = '발인일은 사망일 이후여야 합니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 이전 단계로 이동
  const handlePrev = () => {
    navigate('/obituaries/create');
  };

  // 다음 단계로 이동
  const handleNext = () => {
    if (validateForm()) {
      navigate('/obituaries/create/step3');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary-900 mb-2">
            부고 작성
          </h1>
          <p className="text-sm text-gray-600">
            장례식장 정보를 입력해주세요.
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          currentStep={2}
          totalSteps={3}
          steps={['고인 정보', '장례 정보', '상주 정보']}
          className="mb-8"
        />

        {/* Form */}
        <ObituaryForm
          formData={formData}
          onChange={handleChange}
          errors={errors}
          step="funeral"
        />

        {/* Actions */}
        <div className="flex gap-3 mt-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            className="flex-1"
          >
            이전
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            className="flex-1"
          >
            다음
          </Button>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">안내:</span> 작성 중인 내용은 자동으로 임시 저장됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryCreateStep2;
